import * as PIXI from 'pixi.js';
import { LayoutContainer, LayoutText } from '@pixi/layout/components'; // eslint-disable-line
import { APP_WIDTH, APP_HEIGHT, CARD_HEIGHT, CARD_WIDTH } from '../../utils';

import Tappable from '../../common/containers/Tappable';
import TappableGraphicsButton from '../../common/containers/TappableGraphicsButton';
import TappableTextureButton from '../../common/containers/TappableTextureButton';
import AttentionPopup from '../../common/containers/AttentionPopup';

const scaleFactor = 1.6;
const rectWidth = CARD_WIDTH * scaleFactor;
const rectHeight = CARD_HEIGHT * scaleFactor;

const DECK_LIST_BOX_WIDTH = 900;
const DECK_LIST_BOX_HEIGHT = 1900;
const DECK_LIST_BOX_HEADER_HEIGHT = 150;
const DECK_LIST_BOX_FOOTER_HEIGHT = 350;
const DECK_LIST_BOX_BODY_HEIGHT = DECK_LIST_BOX_HEIGHT - DECK_LIST_BOX_HEADER_HEIGHT - DECK_LIST_BOX_FOOTER_HEIGHT;
const DECK_TYPE_TABS_CONTAINER_HEIGHT = 150;
const ACTION_BUTTONS_CONTAINER_HEIGHT = DECK_LIST_BOX_FOOTER_HEIGHT - DECK_TYPE_TABS_CONTAINER_HEIGHT;
const DECK_LIST_BOX_HEADER_BORDER_WIDTH = 0;
const DECK_LIST_BOX_BORDER_WIDTH = 6;

const TabTypes = {
    MY_DECKS: 'myDecks',
    PREBUILT_DECKS: 'prebuiltDecks',
};

export default class DecksManager {
    constructor(decks = [], selectedDeckId = null) {
        this.container = new Tappable();
        this.container.label = 'DecksContainer';
        this.container.layout = {
            width: APP_WIDTH,
            height: APP_HEIGHT,
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
        };
        
        this.container.onTap = () => {
            this.onBackButtonClick?.();
        };

        this.decks = decks;
        this.highlightedDeckForSelection = selectedDeckId;

        const selectedDeck = decks.find(deck => deck.id === selectedDeckId);
        if (selectedDeck) {
            this.selectedTab = selectedDeck.isPrebuilt ? TabTypes.PREBUILT_DECKS : TabTypes.MY_DECKS;
        }
    }

    static hasLoadedStaticAssets() {
        return Boolean(PIXI.Assets.get('decksBackButton'));
    }

    static hasLoadedLeader(leader) {
        return Boolean(leader && PIXI.Assets.get(`/proxy/image/${leader}.png`));
    }

    static async loadStaticAssets() {
        await PIXI.Assets.load([
            { src: '/assets/decks/back_button.png', alias: 'decksBackButton' },
            { src: '/assets/decks/delete_deck.png', alias: 'deleteDeck' },
            { src: '/assets/decks/new_deck_plus.png', alias: 'newDeckPlus' },
        ]);
        await AttentionPopup.loadAssets();
    }

    static async loadDecksAssets(decks) {
        const leadersArray = decks.map(deck => ({ src: `/proxy/image/${deck.leader}.png` }));
        await PIXI.Assets.load([
            ...leadersArray,
        ]);
    }

    async loadAssets() {
        await DecksManager.loadStaticAssets();
        await DecksManager.loadDecksAssets(this.decks);
    }

    attachToPixiApp(app) {
        app.stage.addChild(this.container);
    }

    detachFromPixiApp(app) {
        app.stage.removeChild(this.container);
    }

    buildTabsButtons(rebuild = false) {
        const TABS_BUTTONS_CONTAINER_WIDTH = 680;
        const TABS_BUTTONS_CONTAINER_HEIGHT = 110;
        const TABS_BUTTONS_CONTAINER_PADDING = 10;

        const TABS_BUTTONS_WIDTH = 320;
        const TABS_BUTTONS_HEIGHT = TABS_BUTTONS_CONTAINER_HEIGHT - TABS_BUTTONS_CONTAINER_PADDING * 2;

        const SELECTED_COLOR = '#4a5f82';
        const UNSELECTED_COLOR = '#232E41';

        if (!rebuild) {
            this.deckTypeTabsContainer = new LayoutContainer();
            this.deckTypeTabsContainer.label = 'deckTypeTabsContainer';
            this.deckTypeTabsContainer.layout = {
                width: DECK_LIST_BOX_WIDTH,
                height: DECK_TYPE_TABS_CONTAINER_HEIGHT,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#4a5f82',
                paddingLeft: (DECK_LIST_BOX_WIDTH - TABS_BUTTONS_CONTAINER_WIDTH) / 2 + TABS_BUTTONS_CONTAINER_PADDING,
                paddingRight: (DECK_LIST_BOX_WIDTH - TABS_BUTTONS_CONTAINER_WIDTH) / 2 + TABS_BUTTONS_CONTAINER_PADDING,
            };
            this.deckListBoxFooterContainer.addChild(this.deckTypeTabsContainer);
        } else {
            this.deckTypeTabsContainer.removeChildren();
        }

        const tabButtonsBackground = new PIXI.Graphics();
        tabButtonsBackground.layout = {
            width: TABS_BUTTONS_CONTAINER_WIDTH,
            height: TABS_BUTTONS_CONTAINER_HEIGHT,
            position: 'absolute',
        };
        tabButtonsBackground.x = -TABS_BUTTONS_CONTAINER_PADDING;
        tabButtonsBackground.roundRect(0, 0, TABS_BUTTONS_CONTAINER_WIDTH, TABS_BUTTONS_CONTAINER_HEIGHT, TABS_BUTTONS_CONTAINER_HEIGHT / 2);
        tabButtonsBackground.fill({ color: UNSELECTED_COLOR });
        this.deckTypeTabsContainer.addChild(tabButtonsBackground);

        const isMyDecksSelected = this.selectedTab === TabTypes.MY_DECKS;
        this.myDecksButton = new TappableGraphicsButton({
            text: 'My Decks',
            width: TABS_BUTTONS_WIDTH,
            height: TABS_BUTTONS_HEIGHT,
            cornerRadius: TABS_BUTTONS_HEIGHT / 2,
            backgroundColor: isMyDecksSelected ? SELECTED_COLOR : UNSELECTED_COLOR,
            borderColor: isMyDecksSelected ? SELECTED_COLOR : UNSELECTED_COLOR,
        });
        this.deckTypeTabsContainer.addChild(this.myDecksButton);
        this.myDecksButton.onButtonTap = () => {
            this.switchTab(TabTypes.MY_DECKS);
        };

        const isPrebuiltDecksSelected = this.selectedTab === TabTypes.PREBUILT_DECKS;
        this.prebuiltDecksButton = new TappableGraphicsButton({
            text: 'Starters',
            width: TABS_BUTTONS_WIDTH,
            height: TABS_BUTTONS_HEIGHT,
            cornerRadius: TABS_BUTTONS_HEIGHT / 2,
            backgroundColor: isPrebuiltDecksSelected ? SELECTED_COLOR : UNSELECTED_COLOR,
            borderColor: isPrebuiltDecksSelected ? SELECTED_COLOR : UNSELECTED_COLOR,
        });
        this.deckTypeTabsContainer.addChild(this.prebuiltDecksButton);
        this.prebuiltDecksButton.onButtonTap = () => {
            this.switchTab(TabTypes.PREBUILT_DECKS);
        };
    }

    buildActionButtons() {
        const ACTION_BUTTONS_WIDTH = 350;
        const ACTION_BUTTONS_HEIGHT = 100;

        const gradient = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0, color: '#f9fafc' },
                { offset: 1, color: '#a8bcda' },
            ],
        });

        this.actionButtonsContainer = new LayoutContainer();
        this.actionButtonsContainer.label = 'actionButtonsContainer';
        this.actionButtonsContainer.layout = {
            width: DECK_LIST_BOX_WIDTH,
            height: ACTION_BUTTONS_CONTAINER_HEIGHT,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 50,
        };
        this.deckListBoxFooterContainer.addChild(this.actionButtonsContainer);

        const BOTTOM_BORDER_RADIUS = 25;
        const actionButtonsBackground = new PIXI.Graphics();
        actionButtonsBackground.layout = {
            width: DECK_LIST_BOX_WIDTH,
            height: ACTION_BUTTONS_CONTAINER_HEIGHT,
            position: 'absolute',
        };
        actionButtonsBackground.moveTo(0, 0);
        actionButtonsBackground.lineTo(DECK_LIST_BOX_WIDTH, 0);
        actionButtonsBackground.lineTo(DECK_LIST_BOX_WIDTH, ACTION_BUTTONS_CONTAINER_HEIGHT - BOTTOM_BORDER_RADIUS);
        actionButtonsBackground.arcTo(DECK_LIST_BOX_WIDTH, ACTION_BUTTONS_CONTAINER_HEIGHT, DECK_LIST_BOX_WIDTH - BOTTOM_BORDER_RADIUS, ACTION_BUTTONS_CONTAINER_HEIGHT, BOTTOM_BORDER_RADIUS);
        actionButtonsBackground.lineTo(BOTTOM_BORDER_RADIUS, ACTION_BUTTONS_CONTAINER_HEIGHT);
        actionButtonsBackground.arcTo(0, ACTION_BUTTONS_CONTAINER_HEIGHT, 0, ACTION_BUTTONS_CONTAINER_HEIGHT - BOTTOM_BORDER_RADIUS, BOTTOM_BORDER_RADIUS);
        actionButtonsBackground.closePath();
        // actionButtonsBackground.rect(0, 0, DECK_LIST_BOX_WIDTH, ACTION_BUTTONS_CONTAINER_HEIGHT);
        actionButtonsBackground.fill(gradient);
        // actionButtonsBackground.stroke({ color: '#4a5f82', width: 5 });
        this.actionButtonsContainer.addChild(actionButtonsBackground);

        this.editDeckButton = new TappableGraphicsButton({
            text: 'Edit',
            width: ACTION_BUTTONS_WIDTH,
            height: ACTION_BUTTONS_HEIGHT,
            backgroundColor: '#dcdbdc',
            borderColor: '#f1efef',
            outerBorderColor: '#b5b6b8',
            borderWidth: 4,
            fontColor: '#000000',
            fontSize: 45,
            fontWeight: '600',
        });
        this.actionButtonsContainer.addChild(this.editDeckButton);
        this.editDeckButton.onButtonTap = () => {
            this.onEditDeckButtonClick?.(this.highlightedDeckForSelection);
        };

        this.selectDeckButton = new TappableGraphicsButton({
            text: 'Select',
            width: ACTION_BUTTONS_WIDTH,
            height: ACTION_BUTTONS_HEIGHT,
            backgroundColor: '#e35043',
            borderColor: '#f27464',
            outerBorderColor: '#bb614d',
            borderWidth: 4,
            fontColor: '#ffffff',
            fontSize: 45,
            fontWeight: '600',
        });
        this.actionButtonsContainer.addChild(this.selectDeckButton);
        this.selectDeckButton.onButtonTap = () => {
            this.onSelectDeckButtonClick?.(this.highlightedDeckForSelection);
        };
    }

    buildFooter() {
        this.deckListBoxFooterContainer = new LayoutContainer();
        this.deckListBoxFooterContainer.label = 'deckListBoxFooterContainer';
        this.deckListBoxFooterContainer.x = 0;
        this.deckListBoxFooterContainer.y = -DECK_LIST_BOX_BORDER_WIDTH * 2;
        this.deckListBoxFooterContainer.layout = {
            width: DECK_LIST_BOX_WIDTH,
            height: DECK_LIST_BOX_FOOTER_HEIGHT,
            flexDirection: 'column',
        };
        this.deckListBoxContainer.addChild(this.deckListBoxFooterContainer);

        this.buildTabsButtons();
        this.buildActionButtons();
    }

    resize(width, height) {
        if (!this.background || (this.width === width && this.height === height)) return;

        this.width = width;
        this.height = height;
        this.container.layout.setStyle({ width, height });
        this.background.clear();
        this.background.rect(0, 0, width, height);
        this.background.fill({ color: '#000000' });
        this.confirmDeleteModal?.resize(width, height);
    }

    buildMainLayout(width, height) {
        this.container.removeChildren();
        this.width = width;
        this.height = height;
        this.container.layout = {
            width: width,
            height: height,
        };

        this.background = new PIXI.Graphics();
        this.background.rect(0, 0, width, height);
        this.background.fill({ color: '#000000' });
        this.background.alpha = 0.5;
        this.container.addChild(this.background);

        this.deckListBoxContainer = new LayoutContainer();
        this.deckListBoxContainer.label = 'deckListBoxContainer';
        this.deckListBoxContainer.x = 0;
        this.deckListBoxContainer.y = 0;
        this.deckListBoxContainer.layout = {
            width: DECK_LIST_BOX_WIDTH,
            height: DECK_LIST_BOX_HEIGHT,
            flexDirection: 'column',
        };
        this.deckListBoxContainer.eventMode = 'static';
        this.deckListBoxContainer.on('pointerdown', (event) => {
            event.stopPropagation();
        });
        this.container.addChild(this.deckListBoxContainer);

        const deckListBoxHeader = new LayoutContainer();
        deckListBoxHeader.label = 'deckListBoxHeader';
        deckListBoxHeader.layout = {
            width: DECK_LIST_BOX_WIDTH,
            height: DECK_LIST_BOX_HEADER_HEIGHT,
            flexDirection: 'row',
            alignItems: 'center',
        };
        this.deckListBoxContainer.addChild(deckListBoxHeader);

        const headerGradient = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0.5, y: 0 },
            end: { x: 0.5, y: 1 },
            colorStops: [
                { offset: 0, color: '#1f2b3f' },
                { offset: 1, color: '#41557c' },
            ],
        });

        const headerBackground = new PIXI.Graphics();
        headerBackground.rect(DECK_LIST_BOX_HEADER_BORDER_WIDTH/2, DECK_LIST_BOX_HEADER_BORDER_WIDTH/2, DECK_LIST_BOX_WIDTH - DECK_LIST_BOX_HEADER_BORDER_WIDTH, DECK_LIST_BOX_HEADER_HEIGHT - DECK_LIST_BOX_HEADER_BORDER_WIDTH);
        headerBackground.fill(headerGradient);
        headerBackground.stroke({ color: '#4a5f82', width: DECK_LIST_BOX_HEADER_BORDER_WIDTH });
        deckListBoxHeader.addChild(headerBackground);

        const headerText = new LayoutText({
            text: 'SELECT A DECK',
            style: {
                fontFamily: 'Open Sans',
                fontSize: 50,
                fontWeight: '700',
                fill: 'white',
            },
        });
        headerText.layout = {
            width: DECK_LIST_BOX_WIDTH,
            height: DECK_LIST_BOX_HEADER_HEIGHT,
            objectPosition: 'center',
        };
        deckListBoxHeader.addChild(headerText);

        const backButton = new PIXI.Sprite(PIXI.Assets.get('decksBackButton'));
        backButton.label = 'backButton';
        backButton.layout = {
            width: 130,
            objectFit: 'cover',
            position: 'absolute',
            left: 40,
        };
        backButton.eventMode = 'static';
        backButton.on('pointerdown', () => {
            this.onBackButtonClick?.();
        });
        deckListBoxHeader.addChild(backButton);

        this.deckListBoxBodyContainer = new LayoutContainer();
        this.deckListBoxBodyContainer.label = 'deckListBoxBodyContainer';
        this.deckListBoxBodyContainer.x = 0;
        this.deckListBoxBodyContainer.y = -DECK_LIST_BOX_BORDER_WIDTH;

        this.deckListBoxBodyContainer.layout = {
            width: DECK_LIST_BOX_WIDTH,
            height: DECK_LIST_BOX_BODY_HEIGHT,
            flexDirection: 'row',
            alignContent: 'flex-start',
            overflow: 'scroll',
            gap: 60,
            padding: 60,
            flexWrap: 'wrap',
            justifyContent: 'center',
            backgroundColor: '#1d2129',
            borderColor: '#41557c',
            borderWidth: DECK_LIST_BOX_BORDER_WIDTH,
        };

        this.deckListBoxContainer.addChild(this.deckListBoxBodyContainer);

        this.buildFooter();

        this.confirmDeleteModal = new AttentionPopup('Are you sure you want to delete this deck?', width, height);
        this.confirmDeleteModal.visible = false;
        this.confirmDeleteModal.onCancelButtonClicked = () => {
            this.hideConfirmDeleteModal();
        };
        this.confirmDeleteModal.onProceedButtonClicked = () => {
            this.onConfirmDeleteDeck?.(this.deckSelectedForDelete);
            this.hideConfirmDeleteModal();
        };
        this.container.addChild(this.confirmDeleteModal);
    }

    #createNewDeckButtonContainer() {
        const rectRadius = 10 * scaleFactor;

        const container = new Tappable();
        container.layout = {
            width: rectWidth,
            flexDirection: 'column',
            gap: 25,
        };
        container.onTap = () => {
            this.onNewDeckButtonClick?.();
        };

        const newDeckButtonContainer = new LayoutContainer();
        newDeckButtonContainer.label = 'newDeckButtonContainer';
        newDeckButtonContainer.layout = {
            width: rectWidth,
            height: rectHeight,
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
        };
        container.addChild(newDeckButtonContainer);

        const newDeckButtonGradient = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0.5, y: 0 },
            end: { x: 0.5, y: 1 },
            colorStops: [
                { offset: 0, color: '#0a1620' },
                { offset: 1, color: '#303a56' },
            ],
        });

        const graphics = new PIXI.Graphics();
        graphics.roundRect(0, 0, rectWidth, rectHeight, rectRadius);
        graphics.fill(newDeckButtonGradient);
        graphics.stroke({ color: '#4a5f82', width: 3 * scaleFactor });

        graphics.layout = {
            width: rectWidth,
            height: rectHeight,
            objectFit: 'fill',
            position: 'absolute',
            top: 0,
            left: 0,
        }

        newDeckButtonContainer.addChild(graphics);

        const newDeckPlus = new PIXI.Sprite(PIXI.Assets.get('newDeckPlus'));
        newDeckPlus.layout = {
            width: 77,
            height: 84,
        };
        newDeckButtonContainer.addChild(newDeckPlus);

        const newDeckText = new LayoutText({
            text: 'New Deck',
            style: {
                fontFamily: 'Open Sans',
                fontSize: 40,
                fontWeight: '600',
                fill: 'white',
                wordWrap: true,
                align: 'center',
            },
        });
        newDeckText.layout = {
            width: rectWidth,
            height: 100,
            objectPosition: 'top',
        };
        container.addChild(newDeckText);

        return container;
    }
    
    buildDeckList() {
        this.deckListBoxBodyContainer.removeChildren();

        let deckList = [];
        if (this.selectedTab === 'myDecks') {
            deckList = this.decks.filter(deck => !deck.isPrebuilt);
        } else if (this.selectedTab === 'prebuiltDecks') {
            deckList = this.decks.filter(deck => deck.isPrebuilt);
        }

        const deckListLength = deckList.length;

        if (this.selectedTab === 'myDecks') {
            const newDeckButtonContainer = this.#createNewDeckButtonContainer();
            this.deckListBoxBodyContainer.addChild(newDeckButtonContainer);
        }

        for (let i = 0; i < deckListLength; i++) {
            const container = new Tappable();
            container.layout = {
                width: rectWidth,
                flexDirection: 'column',
                gap: 25,
            };
            container.label = `deckContainer${i}`;

            const leaderTexture = PIXI.Assets.get(`/proxy/image/${deckList[i].leader}.png`);
            const leaderTappableTextureButton = new TappableTextureButton({
                texture: leaderTexture,
                width: rectWidth,
                height: rectHeight,
            });
            container.addChild(leaderTappableTextureButton);

            const selectionGraphics = new PIXI.Graphics();
            selectionGraphics.roundRect(0, 0, rectWidth, rectHeight, 10);
            selectionGraphics.stroke({ color: 'rgb(76 184 192)', width: 10 * scaleFactor });
            selectionGraphics.visible = this.highlightedDeckForSelection === deckList[i].id;
            leaderTappableTextureButton.addChild(selectionGraphics);

            const text = new LayoutText({
                text: deckList[i].name,
                style: {
                    fontFamily: 'Open Sans',
                    fontSize: 40,
                    fontWeight: '600',
                    fill: 'white',
                    wordWrap: true,
                    align: 'center',
                },
            });

            text.layout = {
                width: rectWidth,
                height: 100,
                objectPosition: 'top',
            }

            container.addChild(text);

            let deleteButtonClicked = false;
            if (!deckList[i].isPrebuilt) {
                const deleteButtonContainer = new Tappable();
                deleteButtonContainer.layout = {
                    position: 'absolute',
                    top: 20,
                    right: 20,
                };

                const deleteButtonSprite = new PIXI.Sprite(PIXI.Assets.get('deleteDeck'));
                deleteButtonSprite.layout = {
                    width: 100,
                    height: 100,
                    objectFit: 'contain',
                };
                deleteButtonContainer.addChild(deleteButtonSprite);
                container.addChild(deleteButtonContainer);

                deleteButtonContainer.onTap = () => {
                    deleteButtonClicked = true;
                    setTimeout(() => {
                        deleteButtonClicked = false;
                    }, 1);
                    
                    this.deckSelectedForDelete = deckList[i].id;
                    this.showConfirmDeleteModal();
                };
            }

            container.onTap = () => {
                if (deleteButtonClicked) return;
                selectionGraphics.visible = !selectionGraphics.visible;
                this.setHighlightedDeckForSelection(deckList[i].id);
            };

            this.deckListBoxBodyContainer.addChild(container);
        }

        if ((this.selectedTab === 'myDecks' && deckListLength%2 === 0) ||
            (this.selectedTab === 'prebuiltDecks' && deckListLength%2 !== 0)) {
            const emptySpace = new PIXI.Container();
            emptySpace.layout = {
                width: rectWidth,
                height: rectHeight,
            }
            this.deckListBoxBodyContainer.addChild(emptySpace);
        }

        this.deckListBoxBodyContainer._trackpad.yAxis._hasStopped = false;
        // this.deckListBoxBodyContainer._trackpad.yAxis.position = 0;
    }

    async setDecks(decks) {
        this.decks = decks;
        const selectedDeck = decks.find((deck) => deck.id === this.highlightedDeckForSelection);
        if (selectedDeck) {
            this.selectedTab = selectedDeck.isPrebuilt ? TabTypes.PREBUILT_DECKS : TabTypes.MY_DECKS;
        }
        await this.loadAssets();
        if (this.deckTypeTabsContainer) {
            this.buildTabsButtons(true);
        }
        this.buildDeckList();
    }

    setSelectedDeckId(deckId) {
        this.highlightedDeckForSelection = deckId;
        const selectedDeck = (this.decks || []).find((deck) => deck.id === deckId);
        if (!selectedDeck) {
            if (this.deckListBoxBodyContainer) {
                this.buildDeckList();
            }
            return;
        }

        const tab = selectedDeck.isPrebuilt ? TabTypes.PREBUILT_DECKS : TabTypes.MY_DECKS;
        if (this.selectedTab !== tab) {
            this.switchTab(tab);
            return;
        }
        if (this.deckListBoxBodyContainer) {
            this.buildDeckList();
        }
    }

    switchTab(tab) {
        this.selectedTab = tab;
        // this.setHighlightedDeckForSelection(null);
        this.buildTabsButtons(true);
        this.buildDeckList();
    }

    showConfirmDeleteModal() {
        this.confirmDeleteModal.visible = true;
    }

    hideConfirmDeleteModal() {
        this.confirmDeleteModal.visible = false;
    }

    setHighlightedDeckForSelection(deckId) {
        this.highlightedDeckForSelection = deckId;
        this.buildDeckList();
    }
}